import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/api';
import Logo from '@/components/Logo';
import { CheckCircle2, Loader2, ChevronRight, ChevronLeft, Building2, User, Landmark, Users, FileText, PenTool, AlertCircle, Save, Upload, X, File, Paperclip } from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const BUSINESS_TYPES = ['Corporation', 'Partnership', 'Limited Liability Company (LLC)', 'Individual/Sole Proprietor'];

const STEPS = [
  { num: 1, label: 'Business Info', icon: Building2 },
  { num: 2, label: 'Principal Owner', icon: User },
  { num: 3, label: 'Bank Reference', icon: Landmark },
  { num: 4, label: 'Credit References', icon: Users },
  { num: 5, label: 'Tax & Terms', icon: FileText },
  { num: 6, label: 'Signature', icon: PenTool },
];

// Input component - defined OUTSIDE to prevent re-render focus loss
const FormInput = ({ label, required, value, onChange, type = 'text', placeholder = '', className = '' }) => (
  <div className={className}>
    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>
      {label}{required && ' *'}
    </label>
    <input type={type} required={required} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-11 px-4 rounded-md font-['IBM_Plex_Sans'] text-sm focus:outline-none transition-colors"
      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
  </div>
);

const FormSelect = ({ label, required, value, onChange, options, className = '' }) => (
  <div className={className}>
    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>
      {label}{required && ' *'}
    </label>
    <select required={required} value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full h-11 px-4 rounded-md font-['IBM_Plex_Sans'] text-sm focus:outline-none transition-colors"
      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const FormCheckbox = ({ label, checked, onChange }) => (
  <div className="flex items-center gap-3 cursor-pointer group" onClick={onChange}>
    <div className="w-5 h-5 rounded-md flex items-center justify-center transition-colors flex-shrink-0"
      style={{ background: checked ? 'var(--primary)' : 'var(--bg)', border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}` }}>
      {checked && <CheckCircle2 size={12} style={{ color: 'white' }} />}
    </div>
    <span className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-primary)' }}>{label}</span>
  </div>
);

const emptyForm = {
  business_info: {
    legal_business_name: '', trade_names_dba: '', street_address: '', city: '', state: '', zip_code: '',
    mailing_same_as_physical: true,
    mailing_street_address: '', mailing_city: '', mailing_state: '', mailing_zip_code: '',
    additional_addresses: [],
    email: '', phone: '', dot_number: '', fein_number: '', business_type: '', years_in_business: '',
    nature_of_business: '', date_of_organization: '', state_of_organization: '', po_required: false,
    other_names: '', former_name: '', person_responsible_for_bills: '', affiliate_entities: '',
    num_trucks: '', num_trailers: '', credit_amount_requested: ''
  },
  principal_owner: {
    name: '', title: '', ssn_last4: '', email: '', home_address: '', home_city: '', home_state: '', home_zip: '', home_phone: ''
  },
  bank_reference: {
    bank_name: '', bank_address: '', account_number_last4: '', contact_name: '', phone: '', fax: ''
  },
  credit_references: [
    { name: '', address: '', account_number: '', phone_email: '' },
    { name: '', address: '', account_number: '', phone_email: '' },
    { name: '', address: '', account_number: '', phone_email: '' }
  ],
  tax_status: { is_taxable: true, tax_exemption_number: '' },
  signature: { printed_name: '', title: '', date: '', agreed_to_terms: false },
  personal_guarantee: { guarantor_name: '', guarantor_address: '', guarantor_signature_name: '', guarantor_date: '', agreed: false }
};

// Document types for upload
const DOCUMENT_TYPES = [
  { id: 'articles_of_incorporation', label: 'Articles of Incorporation', hint: 'State filing documents for your business entity' },
  { id: 'tax_certificate', label: 'Sales Tax Certificate', hint: 'Required if you are tax exempt' },
  { id: 'drivers_license', label: "Owner's Driver License", hint: 'Front and back of primary owner ID' },
  { id: 'insurance_certificate', label: 'Certificate of Insurance', hint: 'Current COI for your fleet' },
  { id: 'authority_letter', label: 'Operating Authority / MC Letter', hint: 'FMCSA operating authority documentation' },
  { id: 'other', label: 'Other Supporting Documents', hint: 'Any additional documents that may help your application' },
];

export default function Apply() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [refId, setRefId] = useState('');
  const [intakeToken, setIntakeToken] = useState(token || '');
  const [form, setForm] = useState(emptyForm);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    initForm();
  }, []);

  const initForm = async () => {
    if (token) {
      // Load existing intake by token
      try {
        const res = await api.get(`/intake/form/${token}`);
        if (res.data.status === 'SUBMITTED') {
          setSubmitted(true);
          setRefId(res.data.submitted_at?.slice(0, 8) || '');
        } else {
          setForm(prev => ({ ...prev, ...res.data.form_data }));
          setStep(res.data.current_step || 1);
        }
        // Load existing documents
        await loadDocuments(token);
      } catch (e) {
        setError(e.response?.data?.detail || 'Invalid or expired link');
      }
    } else {
      // Start new self-serve intake
      try {
        const res = await api.post('/intake/start');
        setIntakeToken(res.data.token);
        // Update URL without reload
        window.history.replaceState({}, '', `/apply?token=${res.data.token}`);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to start application. Please try again.');
      }
    }
    setLoading(false);
  };

  const loadDocuments = async (tkn) => {
    try {
      const res = await api.get(`/intake/form/${tkn}/documents`);
      setDocuments(res.data.documents || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!intakeToken) return;
    
    setUploading(true);
    setUploadError('');
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 10MB.');
      setUploading(false);
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload PDF, PNG, JPG, or WEBP files.');
      setUploading(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post(`/intake/form/${intakeToken}/upload?document_type=${documentType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Add to local documents state
      setDocuments(prev => [...prev, {
        id: res.data.upload_id,
        document_type: documentType,
        filename: res.data.filename,
        size: file.size,
        uploaded_at: new Date().toISOString(),
        status: 'UPLOADED'
      }]);
      
    } catch (e) {
      setUploadError(e.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (uploadId) => {
    if (!intakeToken) return;
    
    try {
      await api.delete(`/intake/form/${intakeToken}/upload/${uploadId}`);
      setDocuments(prev => prev.filter(d => d.id !== uploadId));
    } catch (e) {
      setUploadError(e.response?.data?.detail || 'Failed to delete document.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const updateField = (section, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateCreditRef = (index, field, value) => {
    setForm(prev => {
      const refs = [...prev.credit_references];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, credit_references: refs };
    });
  };

  const saveDraft = async () => {
    if (!intakeToken) return;
    setSaving(true);
    try {
      await api.post(`/intake/form/${intakeToken}/save`, { form_data: form, current_step: step });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await saveDraft();
    setStep(s => Math.min(s + 1, 6));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.business_info.legal_business_name) {
      setError('Business name is required');
      return;
    }
    if (!form.business_info.email) {
      setError('Business email is required');
      return;
    }
    if (!form.signature.agreed_to_terms) {
      setError('You must agree to the terms and conditions');
      return;
    }
    
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`/intake/form/${intakeToken}/submit`, { form_data: form, current_step: 6 });
      setRefId(res.data.reference_id);
      setSubmitted(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
      <Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} />
    </div>
  );

  if (error && !form.business_info.legal_business_name) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)' }}>
      <div className="text-center max-w-md">
        <AlertCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--error)' }} />
        <h2 className="font-['Manrope'] font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Unable to Load Application</h2>
        <p className="font-['IBM_Plex_Sans'] mb-6" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <a href="/login" className="btn-primary inline-flex items-center gap-2">Return to Login</a>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)' }}>
      <div className="text-center max-w-md animate-fade-in">
        <div className="w-20 h-20 rounded-md flex items-center justify-center mx-auto mb-6" style={{ background: 'var(--success-muted)', border: '2px solid var(--success)' }}>
          <CheckCircle2 size={40} style={{ color: 'var(--success)' }} />
        </div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--text-faint)' }}>// APPLICATION RECEIVED</p>
        <h2 className="font-['Manrope'] font-extrabold text-3xl mb-3" style={{ color: 'var(--text-primary)' }}>Thank You!</h2>
        <p className="font-['IBM_Plex_Sans'] mb-2" style={{ color: 'var(--text-muted)' }}>
          Our team will review your application and contact you within <strong style={{ color: 'var(--primary)' }}>2 business hours</strong>.
        </p>
        <p className="font-['JetBrains_Mono'] text-sm mb-8" style={{ color: 'var(--primary)' }}>Reference: <strong>{refId}</strong></p>
        
        <div className="p-5 rounded-md text-left" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider mb-3" style={{ color: 'var(--text-faint)' }}>What happens next</p>
          {['Our accounting team reviews your credit application', 'Sales calls to discuss your fleet needs', 'Upon approval, we send your service agreement', 'You get portal access and onboarding begins'].map((s, i) => (
            <div key={i} className="flex items-start gap-3 mb-2">
              <span className="font-['JetBrains_Mono'] text-xs font-bold mt-0.5" style={{ color: 'var(--primary)' }}>{i + 1}.</span>
              <span className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>{s}</span>
            </div>
          ))}
        </div>
        
        <p className="mt-6 font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>
          Already a client? <a href="/login" className="hover:underline" style={{ color: 'var(--primary)' }}>Sign in to your portal →</a>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <header className="border-b px-6 py-4" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div>
              <span className="font-['Manrope'] font-extrabold text-sm" style={{ color: 'var(--text-primary)' }}>Millennials </span>
              <span className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--primary)' }}>Maintenance</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastSaved && (
              <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>
                Saved at {lastSaved}
              </span>
            )}
            <button onClick={saveDraft} disabled={saving} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-['IBM_Plex_Sans'] transition-colors"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
              <Save size={12} /> {saving ? 'Saving...' : 'Save Draft'}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
            {STEPS.map(({ num, label, icon: Icon }) => (
              <div key={num} className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => num < step && setStep(num)} disabled={num > step}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-['IBM_Plex_Sans'] font-medium transition-all ${num === step ? 'shadow-md' : ''}`}
                  style={{ 
                    background: num === step ? 'var(--primary)' : num < step ? 'var(--surface)' : 'transparent',
                    color: num === step ? 'white' : num < step ? 'var(--primary)' : 'var(--text-faint)',
                    border: `1px solid ${num <= step ? 'var(--primary)' : 'var(--border)'}`,
                    cursor: num > step ? 'default' : 'pointer'
                  }}>
                  <Icon size={12} />
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{num}</span>
                </button>
                {num < 6 && <ChevronRight size={14} style={{ color: 'var(--border)' }} className="flex-shrink-0" />}
              </div>
            ))}
          </div>

          {/* Form Content */}
          <div className="rounded-md p-6 sm:p-8" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            {error && (
              <div className="mb-6 p-4 rounded-md flex items-center gap-3" style={{ background: 'var(--error-muted)', border: '1px solid var(--error)' }}>
                <AlertCircle size={16} style={{ color: 'var(--error)' }} />
                <span className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--error)' }}>{error}</span>
              </div>
            )}

            {/* Step 1: Business Information */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 1 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Business Information</h1>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>Tell us about your company</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Legal Business Name" required value={form.business_info.legal_business_name} onChange={v => updateField('business_info', 'legal_business_name', v)} className="md:col-span-2" />
                  <FormInput label="Trade Names / DBAs" value={form.business_info.trade_names_dba} onChange={v => updateField('business_info', 'trade_names_dba', v)} className="md:col-span-2" />
                  <FormInput label="Street Address" required value={form.business_info.street_address} onChange={v => updateField('business_info', 'street_address', v)} className="md:col-span-2" />
                  <FormInput label="City" required value={form.business_info.city} onChange={v => updateField('business_info', 'city', v)} />
                  <div className="grid grid-cols-2 gap-4">
                    <FormSelect label="State" required value={form.business_info.state} onChange={v => updateField('business_info', 'state', v)} options={US_STATES} />
                    <FormInput label="Zip Code" required value={form.business_info.zip_code} onChange={v => updateField('business_info', 'zip_code', v)} />
                  </div>

                  {/* Mailing Address — toggle for separate billing/mailing location */}
                  <div className="md:col-span-2 rounded-lg p-4" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
                    <FormCheckbox
                      label="Mailing address is the same as the physical address"
                      checked={form.business_info.mailing_same_as_physical}
                      onChange={() => updateField('business_info', 'mailing_same_as_physical', !form.business_info.mailing_same_as_physical)}
                    />
                    {!form.business_info.mailing_same_as_physical && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <FormInput label="Mailing Street Address" required value={form.business_info.mailing_street_address} onChange={v => updateField('business_info', 'mailing_street_address', v)} className="md:col-span-2" />
                        <FormInput label="Mailing City" required value={form.business_info.mailing_city} onChange={v => updateField('business_info', 'mailing_city', v)} />
                        <div className="grid grid-cols-2 gap-4">
                          <FormSelect label="Mailing State" required value={form.business_info.mailing_state} onChange={v => updateField('business_info', 'mailing_state', v)} options={US_STATES} />
                          <FormInput label="Mailing Zip Code" required value={form.business_info.mailing_zip_code} onChange={v => updateField('business_info', 'mailing_zip_code', v)} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Additional addresses — terminal, garage, etc. */}
                  <div className="md:col-span-2 rounded-lg p-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Additional Addresses</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Optional — terminals, garages, owner home, etc.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateField('business_info', 'additional_addresses', [
                          ...(form.business_info.additional_addresses || []),
                          { label: '', street_address: '', city: '', state: '', zip_code: '' },
                        ])}
                        className="px-3 py-1.5 text-xs rounded-md font-medium"
                        style={{ background: 'var(--primary)', color: 'white' }}
                      >
                        + Add Another Address
                      </button>
                    </div>

                    {(form.business_info.additional_addresses || []).map((addr, idx) => (
                      <div key={idx} className="mt-3 pt-3 border-t grid grid-cols-1 md:grid-cols-2 gap-3" style={{ borderColor: 'var(--border)' }}>
                        <div className="md:col-span-2 flex items-center justify-between">
                          <FormInput
                            label={`Address #${idx + 1} Label (e.g. Terminal, Garage)`}
                            value={addr.label}
                            onChange={v => {
                              const next = [...form.business_info.additional_addresses];
                              next[idx] = { ...next[idx], label: v };
                              updateField('business_info', 'additional_addresses', next);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = (form.business_info.additional_addresses || []).filter((_, i) => i !== idx);
                              updateField('business_info', 'additional_addresses', next);
                            }}
                            className="ml-3 text-xs text-red-400 hover:text-red-300"
                          >
                            Remove
                          </button>
                        </div>
                        <FormInput label="Street Address" value={addr.street_address} onChange={v => {
                          const next = [...form.business_info.additional_addresses];
                          next[idx] = { ...next[idx], street_address: v };
                          updateField('business_info', 'additional_addresses', next);
                        }} className="md:col-span-2" />
                        <FormInput label="City" value={addr.city} onChange={v => {
                          const next = [...form.business_info.additional_addresses];
                          next[idx] = { ...next[idx], city: v };
                          updateField('business_info', 'additional_addresses', next);
                        }} />
                        <div className="grid grid-cols-2 gap-3">
                          <FormSelect label="State" value={addr.state} onChange={v => {
                            const next = [...form.business_info.additional_addresses];
                            next[idx] = { ...next[idx], state: v };
                            updateField('business_info', 'additional_addresses', next);
                          }} options={US_STATES} />
                          <FormInput label="Zip Code" value={addr.zip_code} onChange={v => {
                            const next = [...form.business_info.additional_addresses];
                            next[idx] = { ...next[idx], zip_code: v };
                            updateField('business_info', 'additional_addresses', next);
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <FormInput label="Email" required type="email" value={form.business_info.email} onChange={v => updateField('business_info', 'email', v)} />
                  <FormInput label="Phone" required type="tel" value={form.business_info.phone} onChange={v => updateField('business_info', 'phone', v)} />
                  <FormInput label="DOT #" value={form.business_info.dot_number} onChange={v => updateField('business_info', 'dot_number', v)} />
                  <FormInput label="FEIN #" value={form.business_info.fein_number} onChange={v => updateField('business_info', 'fein_number', v)} />
                  <FormSelect label="Business Type" required value={form.business_info.business_type} onChange={v => updateField('business_info', 'business_type', v)} options={BUSINESS_TYPES} />
                  <FormInput label="Years in Business" value={form.business_info.years_in_business} onChange={v => updateField('business_info', 'years_in_business', v)} />
                  <FormInput label="Nature of Business" value={form.business_info.nature_of_business} onChange={v => updateField('business_info', 'nature_of_business', v)} />
                  <FormInput label="Date of Organization" type="date" value={form.business_info.date_of_organization} onChange={v => updateField('business_info', 'date_of_organization', v)} />
                  <FormSelect label="State of Organization" value={form.business_info.state_of_organization} onChange={v => updateField('business_info', 'state_of_organization', v)} options={US_STATES} />
                  <div className="flex items-end">
                    <FormCheckbox label="Purchase Order Required?" checked={form.business_info.po_required} onChange={() => updateField('business_info', 'po_required', !form.business_info.po_required)} />
                  </div>
                </div>

                <div className="pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-4" style={{ color: 'var(--text-faint)' }}>// FLEET & CREDIT INFO</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormInput label="Number of Trucks" type="number" value={form.business_info.num_trucks} onChange={v => updateField('business_info', 'num_trucks', v)} />
                    <FormInput label="Number of Trailers" type="number" value={form.business_info.num_trailers} onChange={v => updateField('business_info', 'num_trailers', v)} />
                    <FormInput label="Credit Amount Requested ($)" value={form.business_info.credit_amount_requested} onChange={v => updateField('business_info', 'credit_amount_requested', v)} placeholder="e.g., 10000" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Principal Owner */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 2 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Principal Owner Information</h1>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>Owner or authorized representative</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Full Name" required value={form.principal_owner.name} onChange={v => updateField('principal_owner', 'name', v)} />
                  <FormInput label="Title" value={form.principal_owner.title} onChange={v => updateField('principal_owner', 'title', v)} />
                  <FormInput label="SSN (Last 4 digits only)" value={form.principal_owner.ssn_last4} onChange={v => updateField('principal_owner', 'ssn_last4', v.slice(0, 4))} placeholder="XXXX" />
                  <FormInput label="Email" type="email" value={form.principal_owner.email} onChange={v => updateField('principal_owner', 'email', v)} />
                  <FormInput label="Home Address" value={form.principal_owner.home_address} onChange={v => updateField('principal_owner', 'home_address', v)} className="md:col-span-2" />
                  <FormInput label="City" value={form.principal_owner.home_city} onChange={v => updateField('principal_owner', 'home_city', v)} />
                  <div className="grid grid-cols-2 gap-4">
                    <FormSelect label="State" value={form.principal_owner.home_state} onChange={v => updateField('principal_owner', 'home_state', v)} options={US_STATES} />
                    <FormInput label="Zip" value={form.principal_owner.home_zip} onChange={v => updateField('principal_owner', 'home_zip', v)} />
                  </div>
                  <FormInput label="Home Phone" type="tel" value={form.principal_owner.home_phone} onChange={v => updateField('principal_owner', 'home_phone', v)} />
                </div>
              </div>
            )}

            {/* Step 3: Bank Reference */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 3 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Bank Reference</h1>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>Your primary banking institution</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Bank Name" value={form.bank_reference.bank_name} onChange={v => updateField('bank_reference', 'bank_name', v)} className="md:col-span-2" />
                  <FormInput label="Bank Address" value={form.bank_reference.bank_address} onChange={v => updateField('bank_reference', 'bank_address', v)} className="md:col-span-2" />
                  <FormInput label="Account # (Last 4 digits)" value={form.bank_reference.account_number_last4} onChange={v => updateField('bank_reference', 'account_number_last4', v.slice(0, 4))} placeholder="XXXX" />
                  <FormInput label="Contact Name" value={form.bank_reference.contact_name} onChange={v => updateField('bank_reference', 'contact_name', v)} />
                  <FormInput label="Phone" type="tel" value={form.bank_reference.phone} onChange={v => updateField('bank_reference', 'phone', v)} />
                  <FormInput label="Fax" value={form.bank_reference.fax} onChange={v => updateField('bank_reference', 'fax', v)} />
                </div>
              </div>
            )}

            {/* Step 4: Credit References */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 4 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Credit References</h1>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>Please provide three trade references</p>
                </div>
                
                {[0, 1, 2].map(i => (
                  <div key={i} className="p-5 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <p className="text-xs font-['JetBrains_Mono'] font-bold mb-4" style={{ color: 'var(--primary)' }}>Reference #{i + 1}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormInput label="Company Name" value={form.credit_references[i].name} onChange={v => updateCreditRef(i, 'name', v)} />
                      <FormInput label="Phone / Email" value={form.credit_references[i].phone_email} onChange={v => updateCreditRef(i, 'phone_email', v)} />
                      <FormInput label="Address" value={form.credit_references[i].address} onChange={v => updateCreditRef(i, 'address', v)} />
                      <FormInput label="Account #" value={form.credit_references[i].account_number} onChange={v => updateCreditRef(i, 'account_number', v)} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 5: Tax Status & Terms */}
            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 5 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Tax Status</h1>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="radio" checked={form.tax_status.is_taxable} onChange={() => updateField('tax_status', 'is_taxable', true)}
                        className="w-4 h-4" style={{ accentColor: 'var(--primary)' }} />
                      <span className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-primary)' }}>Taxable</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="radio" checked={!form.tax_status.is_taxable} onChange={() => updateField('tax_status', 'is_taxable', false)}
                        className="w-4 h-4" style={{ accentColor: 'var(--primary)' }} />
                      <span className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-primary)' }}>Non-Taxable</span>
                    </label>
                  </div>
                  
                  {!form.tax_status.is_taxable && (
                    <FormInput label="Tax Exemption Certificate Number" value={form.tax_status.tax_exemption_number} onChange={v => updateField('tax_status', 'tax_exemption_number', v)} />
                  )}
                </div>

                <div className="pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-4" style={{ color: 'var(--text-faint)' }}>// TERMS & CONDITIONS</p>
                  <div className="p-4 rounded-md mb-4 max-h-48 overflow-y-auto" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <p className="font-['IBM_Plex_Sans'] text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                      By submitting this application, I/we authorize Millennials Maintenance to investigate my/our credit history and background. I/we agree that all purchases made on credit will be paid according to the terms and conditions established by Millennials Maintenance. I/we understand that a service charge may be applied to past-due accounts. I/we agree to pay all collection costs, including reasonable attorney's fees, if this account is placed for collection. I/we certify that all information provided is true and accurate to the best of my/our knowledge.
                    </p>
                  </div>
                </div>

                {/* Document Upload Section */}
                <div className="pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>// SUPPORTING DOCUMENTS</p>
                  <p className="font-['IBM_Plex_Sans'] text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                    Upload any supporting documents to expedite your application review. Accepted formats: PDF, PNG, JPG (max 10MB each).
                  </p>
                  
                  {uploadError && (
                    <div className="mb-4 p-3 rounded-md flex items-center gap-2" style={{ background: 'var(--error-muted)', border: '1px solid var(--error)' }}>
                      <AlertCircle size={14} style={{ color: 'var(--error)' }} />
                      <span className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--error)' }}>{uploadError}</span>
                      <button onClick={() => setUploadError('')} className="ml-auto" style={{ color: 'var(--error)' }}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  
                  {/* Document Type Cards */}
                  <div className="space-y-3">
                    {DOCUMENT_TYPES.map(docType => {
                      const uploadedDocs = documents.filter(d => d.document_type === docType.id);
                      
                      return (
                        <div key={docType.id} className="p-4 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Paperclip size={14} style={{ color: 'var(--primary)' }} />
                                <span className="font-['IBM_Plex_Sans'] font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {docType.label}
                                </span>
                                {uploadedDocs.length > 0 && (
                                  <span className="text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 rounded-full" 
                                    style={{ background: 'var(--success-muted)', color: 'var(--success)' }}>
                                    {uploadedDocs.length} uploaded
                                  </span>
                                )}
                              </div>
                              <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>{docType.hint}</p>
                              
                              {/* Show uploaded files */}
                              {uploadedDocs.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {uploadedDocs.map(doc => (
                                    <div key={doc.id} className="flex items-center gap-2 p-2 rounded" style={{ background: 'var(--surface)' }}>
                                      <File size={14} style={{ color: 'var(--primary)' }} />
                                      <span className="font-['IBM_Plex_Sans'] text-xs flex-1 truncate" style={{ color: 'var(--text-muted)' }}>
                                        {doc.filename}
                                      </span>
                                      <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                                        {formatFileSize(doc.size)}
                                      </span>
                                      <button 
                                        onClick={() => handleDeleteDocument(doc.id)}
                                        className="p-1 rounded hover:bg-red-500/20 transition-colors"
                                        style={{ color: 'var(--error)' }}
                                        data-testid={`delete-doc-${doc.id}`}
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Upload button */}
                            <label className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors hover:opacity-80"
                              style={{ background: 'var(--primary)', color: 'white' }}>
                              {uploading ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Upload size={14} />
                              )}
                              <span className="text-xs font-['IBM_Plex_Sans'] font-medium">Upload</span>
                              <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp"
                                className="hidden"
                                disabled={uploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleFileUpload(docType.id, file);
                                    e.target.value = ''; // Reset input
                                  }
                                }}
                                data-testid={`upload-input-${docType.id}`}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Upload summary */}
                  {documents.length > 0 && (
                    <div className="mt-4 p-3 rounded-md flex items-center gap-2" style={{ background: 'var(--success-muted)', border: '1px solid var(--success)' }}>
                      <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />
                      <span className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--success)' }}>
                        {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 6: Signature */}
            {step === 6 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// STEP 6 OF 6</p>
                  <h1 className="font-['Manrope'] font-extrabold text-2xl mb-2" style={{ color: 'var(--text-primary)' }}>Authorization & Signature</h1>
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>Review and sign your application</p>
                </div>
                
                <div className="p-5 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-['JetBrains_Mono'] font-bold mb-4" style={{ color: 'var(--primary)' }}>AUTHORIZATION SIGNATURE</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput label="Printed Name" required value={form.signature.printed_name} onChange={v => updateField('signature', 'printed_name', v)} />
                    <FormInput label="Title" value={form.signature.title} onChange={v => updateField('signature', 'title', v)} />
                    <FormInput label="Date" type="date" required value={form.signature.date} onChange={v => updateField('signature', 'date', v)} />
                  </div>
                  <div className="mt-4">
                    <FormCheckbox 
                      label="I agree to the terms and conditions and authorize Millennials Maintenance to investigate my credit history."
                      checked={form.signature.agreed_to_terms}
                      onChange={() => updateField('signature', 'agreed_to_terms', !form.signature.agreed_to_terms)}
                    />
                  </div>
                </div>

                <div className="p-5 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-['JetBrains_Mono'] font-bold mb-2" style={{ color: 'var(--primary)' }}>PERSONAL GUARANTEE (Optional)</p>
                  <p className="font-['IBM_Plex_Sans'] text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                    In consideration of credit being extended, the undersigned personally guarantees payment of all amounts owed.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput label="Guarantor Name" value={form.personal_guarantee.guarantor_name} onChange={v => updateField('personal_guarantee', 'guarantor_name', v)} />
                    <FormInput label="Guarantor Address" value={form.personal_guarantee.guarantor_address} onChange={v => updateField('personal_guarantee', 'guarantor_address', v)} />
                    <FormInput label="Signature (Type Name)" value={form.personal_guarantee.guarantor_signature_name} onChange={v => updateField('personal_guarantee', 'guarantor_signature_name', v)} />
                    <FormInput label="Date" type="date" value={form.personal_guarantee.guarantor_date} onChange={v => updateField('personal_guarantee', 'guarantor_date', v)} />
                  </div>
                  {form.personal_guarantee.guarantor_name && (
                    <div className="mt-4">
                      <FormCheckbox 
                        label="I personally guarantee payment of all amounts owed"
                        checked={form.personal_guarantee.agreed}
                        onChange={() => updateField('personal_guarantee', 'agreed', !form.personal_guarantee.agreed)}
                      />
                    </div>
                  )}
                </div>
              </form>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
              {step > 1 ? (
                <button onClick={handleBack} className="flex items-center gap-2 px-5 py-3 rounded-md font-['Manrope'] font-bold text-sm transition-colors"
                  style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                  <ChevronLeft size={16} /> Back
                </button>
              ) : <div />}
              
              {step < 6 ? (
                <button onClick={handleNext} className="btn-primary flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wide">
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={submitting || !form.signature.agreed_to_terms}
                  className="btn-primary flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
                  data-testid="submit-application-btn">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : 'Submit Application'}
                </button>
              )}
            </div>
          </div>

          <p className="text-center font-['IBM_Plex_Sans'] text-xs mt-6" style={{ color: 'var(--text-faint)' }}>
            Already a client? <a href="/login" className="hover:underline" style={{ color: 'var(--primary)' }}>Sign in to your portal →</a>
          </p>
        </div>
      </main>
    </div>
  );
}
